import { ComponentProps, PropsWithChildren, useMemo } from 'react'
import { Avatar } from './Avatar'
import CircularTimerProgress from './Timer'
import { DS } from '@/design-system'
import { useResponsive } from '../../../../screens/new-design/hooks'
import { Platform, StyleSheet, View, ViewStyle } from 'react-native'
import { BotAvatar, TheirAvatar } from '@/screens/profile-screen/edit/AvatarBuilder'
import type { BotSkillLevel } from '@ppwr/poker-engine/utils'

type AvatarTimerProps = {
  startTime?: number
  timeMS: number // in ms
  enable: boolean
  containerStyle?: ViewStyle
  avatarProps: ComponentProps<typeof Avatar>
  kind?: 'human' | 'bot'
  userId?: string
  skillLevel?: BotSkillLevel
}

type RoundTimerProps = PropsWithChildren<{
  startTime?: number
  timeMS: number // in ms
  enable: boolean
  containerStyle?: ViewStyle
  timerProps?: Partial<ComponentProps<typeof CircularTimerProgress>>
}>

export const RoundTimer = ({ enable, timeMS, startTime, containerStyle, children, timerProps }: RoundTimerProps) => {
  const { verticalScale } = useResponsive()

  // const size = verticalScale(40)
  // const stroke = verticalScale(8)
  const size = 40
  const stroke = Platform.OS === 'web' ? 4 : 8

  const initialProgress = useMemo(() => {
    if (!startTime) {
      return 0
    }

    return Math.min((timeMS - startTime) / startTime, 1)
  }, [timeMS, startTime])

  // `children` (the avatar) always renders in this same spot regardless of `enable`,
  // so toggling the ring on/off (turn switches) never unmounts/remounts it and
  // loses its state (e.g. an in-flight query / loading state).
  return (
    <DS.Center style={[containerStyle, { width: size, height: size }]}>
      {enable && (
        <View style={StyleSheet.absoluteFill}>
          <CircularTimerProgress
            initialProgress={initialProgress}
            duration={timeMS}
            size={size}
            strokeWidth={stroke}
            colorMode="step"
            colorStops={[0, 0.85]}
            colors={[DS.palette['Brand/Orange'], DS.palette['Brand/Pink']]}
            trackColor="#F1F4F9"
            clockwise={false}
            {...timerProps}
          />
        </View>
      )}
      {children}
    </DS.Center>
  )
}
export const AvatarTimer = (props: AvatarTimerProps) => {
  const { avatarProps, kind, userId, skillLevel } = props

  const renderAvatar = useMemo(() => {
    if (kind === 'bot') {
      return <BotAvatar skillLevel={skillLevel} size={32} />
    } else if (kind === 'human') {
      if (userId && !avatarProps.video) {
        return <TheirAvatar userId={userId} name={avatarProps.name} size={32} />
      }
    }
    return <Avatar size={4} {...avatarProps} />
  }, [avatarProps, kind, userId, skillLevel])

  return <RoundTimer {...props}>{renderAvatar}</RoundTimer>
}

export default AvatarTimer
